import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FiArrowLeft, FiPlus, FiMessageSquare,
  FiPaperclip, FiX, FiCalendar, FiUser,
  FiSend, FiCheck, FiDownload, FiFileText, FiTrash2, FiEye, FiEdit2, FiFolder, FiChevronDown, FiCopy, FiUsers
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { fmtDate } from '../../utils/istDate';
import { STATUS_META, PRIORITY_META } from './MySpaceTasks';
import tasksApi, { canEditTask, canChangeTaskStatus } from '../../api/tasksApi';
import TaskChecklistIcon from '../shared/TaskChecklistIcon';
import { extractErrorMessage } from '../../finance/api/client';
import CreateTaskModal from './CreateTaskModal';
import ConfirmModal from '../shared/ConfirmModal';

// Clean neutral avatar
function Avatar({ name, size = 26 }) {
  return (
    <div
      className={`font-semibold flex items-center justify-center shrink-0 rounded-full border ${
        name 
          ? 'bg-slate-100 text-slate-700 border-slate-300' 
          : 'bg-slate-50 text-slate-400 border-dashed border-slate-200'
      }`}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      title={name || 'Unassigned'}
    >
      {name ? name.slice(0, 2).toUpperCase() : '—'}
    </div>
  );
}

export default function MySpaceTaskDetails() {
  const { taskId, projectId: paramProjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const me = localStorage.getItem('username') || '';
  const userRole = (localStorage.getItem('role') || '').toUpperCase();
  const isSuperAdmin = userRole.includes('SUPERADMIN') || userRole.includes('TESTER');
  const isAdmin = userRole.includes('ADMIN');

  const [task, setTask] = useState(location.state?.task || null);
  const effectiveProjectId = paramProjectId || task?.projectId || task?.project?.id;
  const [subtasks, setSubtasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState(location.state?.task?.attachments || []);
  const [projectsList, setProjectsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  
  const [loading, setLoading] = useState(!task);
  const [error, setError] = useState('');

  const [activeTab, setActiveTab] = useState('subtasks');
  const [commentDraft, setCommentDraft] = useState('');

  // Load projects & products to resolve display names
  useEffect(() => {
    tasksApi.getProjects().then(res => setProjectsList(Array.isArray(res) ? res : [])).catch(() => {});
    tasksApi.getProducts().then(res => setProductsList(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  // Edit Drawer State
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  // Subtask Modal State
  const [showSubModal, setShowSubModal] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtaskStatus, setSubtaskStatus] = useState('TO_DO');
  const [subtaskDescription, setSubtaskDescription] = useState('');
  const [subtaskDeadLine, setSubtaskDeadLine] = useState('');
  const [uploading, setUploading] = useState(false);

  // PDF / Document Preview State
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (!previewFile) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPreviewFile(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewFile]);

  // Fetch Task Details from list if location.state not passed or forced reload
  const fetchTaskDetails = useCallback(async (force = false) => {
    if (task && !force) return;
    setLoading(true);
    try {
      // 1. Try direct fetch by ID first
      try {
        const directTask = await tasksApi.getTaskById(taskId);
        if (directTask && directTask.taskId) {
          setTask(directTask);
          setAttachments(directTask.attachments || []);
          setLoading(false);
          return;
        }
      } catch (e) {
        // Fallback to array searches if direct fetch fails
      }

      // 2. Search in my-tasks, created-by-me, and team-tasks
      const [myTasks, createdTasks, teamTasks] = await Promise.all([
        tasksApi.getMyTasks().catch(() => []),
        tasksApi.getCreatedByMeTasks().catch(() => []),
        tasksApi.getTeamTasks().catch(() => [])
      ]);
      
      const all = [
        ...(Array.isArray(myTasks) ? myTasks : []),
        ...(Array.isArray(createdTasks) ? createdTasks : []),
        ...(Array.isArray(teamTasks) ? teamTasks : [])
      ];
      const found = all.find(t => String(t.taskId) === String(taskId));
      
      if (found) {
        setTask(found);
        setAttachments(found.attachments || []);
      } else {
        setError('Task not found or you do not have permission to view it.');
      }
    } catch (err) {
      setError('Failed to load task details.');
    } finally {
      setLoading(false);
    }
  }, [taskId, task]);

  // Fetch Subtasks
  const fetchSubtasks = useCallback(async () => {
    try {
      const data = await tasksApi.getSubtasks(taskId);
      setSubtasks(Array.isArray(data) ? data : []);
    } catch {
      setSubtasks([]);
    }
  }, [taskId]);

  // Fetch Comments
  const fetchComments = useCallback(async () => {
    try {
      const data = await tasksApi.getComments(taskId);
      setComments(Array.isArray(data) ? data : []);
    } catch {
      setComments([]);
    }
  }, [taskId]);

  useEffect(() => {
    fetchTaskDetails();
    fetchSubtasks();
    fetchComments();
  }, [fetchTaskDetails, fetchSubtasks, fetchComments]);

  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState({ open: false, loading: false });

  // Update Status
  const handleUpdateStatus = async (newStatus) => {
    if (!canEditTask(task, me)) {
      toast.error('Only the task assignee or creator can edit this task.');
      return;
    }
    try {
      const updated = await tasksApi.updateTaskStatus(taskId, newStatus);
      const title = task?.title || 'Task';
      const statusLabel = STATUS_META[newStatus]?.label || newStatus;
      toast.success(`Task status changed to ${statusLabel}.`);
      setTask(prev => ({ ...prev, ...(updated || {}), taskStatus: newStatus }));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Self Assign
  const handleSelfAssign = async () => {
    try {
      await tasksApi.assignTask(taskId, me);
      setTask(prev => ({ ...prev, username: me }));
      toast.success('Task assigned to yourself!');
      fetchTaskDetails(true);
    } catch {
      toast.error('Failed to self-assign task');
    }
  };

  // Edit Task Handler (supports files & subtasks added in drawer)
  const handleSaveEditTask = async (payload, newFiles, subtasksList = []) => {
    if (!canEditTask(task, me)) {
      toast.error('Only the task assignee or creator can edit this task.');
      return;
    }
    try {
      const updated = await tasksApi.updateTask(taskId, payload);
      
      // If new subtasks were added in the edit drawer
      if (subtasksList && subtasksList.length > 0) {
        for (const st of subtasksList) {
          const title = typeof st === 'string' ? st : st.title;
          if (title && title.trim()) {
            try {
              await tasksApi.createSubtask({
                taskId: Number(taskId),
                subTaskTitle: title.trim(),
                subTaskStatus: 'TO_DO'
              });
            } catch (stErr) {
              console.error('Failed creating subtask during edit:', stErr);
            }
          }
        }
      }

      // If new files were attached in the edit drawer, upload them
      if (newFiles && newFiles.length > 0) {
        for (const file of newFiles) {
          await tasksApi.uploadAttachment(taskId, file);
        }
      }

      toast.success('Task updated successfully.');
      setIsEditDrawerOpen(false);

      // Instantly update task state
      setTask(prev => ({
        ...prev,
        ...(updated || {}),
        title: payload.title || prev.title,
        description: payload.description !== undefined ? payload.description : prev.description,
        username: payload.username || prev.username,
        deadLine: payload.deadLine || prev.deadLine,
        priority: payload.priority || prev.priority,
        taskStatus: payload.taskStatus || prev.taskStatus
      }));

      // Refresh subtasks and comments and full details
      fetchTaskDetails(true);
      fetchSubtasks();
    } catch (err) {
      console.error('Failed to update task:', err);
      toast.error(extractErrorMessage(err) || 'Failed to update task');
      throw err;
    }
  };

  // Delete Task Confirm
  const confirmDeleteTask = async () => {
    setDeleteTaskConfirm(prev => ({ ...prev, loading: true }));
    try {
      await tasksApi.deleteTask(taskId);
      const title = task?.title || 'Task';
      toast.success(`Task "${title}" has been deleted.`);
      setDeleteTaskConfirm({ open: false, loading: false });
      if (location.state?.from) {
        navigate(location.state.from);
      } else if (effectiveProjectId) {
        navigate(`/ProjectDetails/${effectiveProjectId}`);
      } else if (location.pathname.startsWith('/TasksView')) {
        navigate('/TasksView');
      } else {
        navigate('/my-space/tasks');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || 'Failed to delete task');
      setDeleteTaskConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  // Add Subtask
  const handleSaveSubtask = async () => {
    if (!subtaskTitle.trim()) {
      return toast.error('Subtask title is required');
    }
    try {
      const titleText = subtaskTitle.trim();
      const payload = {
        taskId: Number(taskId),
        subTaskTitle: titleText,
        subTaskStatus: subtaskStatus || 'TO_DO'
      };
      if (subtaskDescription.trim()) {
        payload.description = subtaskDescription.trim();
      }
      if (subtaskDeadLine) {
        payload.deadLine = subtaskDeadLine;
      }
      await tasksApi.createSubtask(payload);
      toast.success(`Subtask "${titleText}" has been created.`);
      setShowSubModal(false);
      setSubtaskTitle('');
      setSubtaskDescription('');
      setSubtaskDeadLine('');
      setSubtaskStatus('TO_DO');
      fetchSubtasks();
    } catch (err) {
      console.error('Subtask creation error:', err);
      toast.error(extractErrorMessage(err) || 'Failed to save subtask');
    }
  };

  // Delete Subtask
  const handleDeleteSubtask = async (subTaskId, sTitle) => {
    try {
      await tasksApi.deleteSubtask(subTaskId);
      toast.success(`Subtask "${sTitle || 'Subtask'}" has been deleted.`);
      fetchSubtasks();
    } catch (err) {
      toast.error('Failed to delete subtask');
    }
  };

  // Update Subtask Status
  const handleUpdateSubtaskStatus = async (subTaskId, newStatus, subTaskTitle) => {
    setSubtasks(prev => prev.map(s => {
      const sId = s.subTaskId || s.id || s.taskId;
      if (String(sId) === String(subTaskId)) {
        return { ...s, subTaskStatus: newStatus, taskStatus: newStatus };
      }
      return s;
    }));
    try {
      await tasksApi.updateSubtaskStatus(subTaskId, newStatus, taskId, subTaskTitle);
      const statusLabel = STATUS_META[newStatus]?.label || newStatus;
      toast.success(`Subtask status changed to ${statusLabel}.`);
    } catch (err) {
      toast.error('Failed to update subtask status');
      fetchSubtasks();
    }
  };

  // Post Comment
  const handlePostComment = async () => {
    const text = commentDraft.trim();
    if (!text) return;
    try {
      await tasksApi.createComment(parseInt(taskId), text);
      setCommentDraft('');
      fetchComments();
      toast.success('Comment posted successfully.');
    } catch (err) {
      toast.error('Failed to post comment');
    }
  };

  // Delete Comment
  const handleDeleteComment = async (commentId) => {
    try {
      await tasksApi.deleteComment(commentId);
      toast.success('Comment has been deleted.');
      fetchComments();
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  // Handle File Upload Attachment
  const handleAddAttachment = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const newAtt = await tasksApi.uploadAttachment(taskId, file);
        toast.success(`"${file.name}" attached successfully.`);
        if (newAtt) {
          setAttachments(prev => [...prev, newAtt]);
        }
      }
      fetchTaskDetails(true);
    } catch (err) {
      toast.error('Failed to upload attachment');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Handle Delete Attachment
  const handleDeleteAttachment = async (attachmentId, fileName) => {
    try {
      await tasksApi.deleteAttachment(taskId, attachmentId);
      toast.success(`"${fileName || 'Attachment'}" deleted.`);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      fetchTaskDetails(true);
    } catch (err) {
      toast.error('Failed to delete attachment');
    }
  };

  if (loading) return <div className="py-20 text-center text-slate-500 font-medium text-sm">Loading task details...</div>;
  if (error) return <div className="p-6 text-center text-rose-500 bg-rose-50 rounded-xl m-6 border border-rose-100 text-sm">{error}</div>;
  if (!task) return <div className="py-20 text-center text-slate-500 text-sm">Task not found.</div>;

  const statusKey = STATUS_META[task.taskStatus] ? task.taskStatus : 'TO_DO';
  const sm = STATUS_META[statusKey];

  const prioKey = PRIORITY_META[task.priority] ? task.priority : 'MEDIUM';
  const pm = PRIORITY_META[prioKey];
  const PriorityIcon = pm.Icon;

  const isOverdue = Boolean(
    task.deadLine &&
    task.taskStatus !== 'COMPLETED' &&
    new Date(task.deadLine).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
  );

  const normalizeUsername = (u) => String(u || '').trim().toLowerCase().split('@')[0];
  const myNormUser = normalizeUsername(me);
  const taskCreatorNorm = normalizeUsername(task.createdBy || task.createdByName);

  const canEdit = canEditTask(task, me);
  // Delete option is strictly restricted to the user who created it ("created by")
  const isCreator = Boolean(taskCreatorNorm && myNormUser && taskCreatorNorm === myNormUser);
  const canDelete = isCreator;

  const canDeleteSubtask = (sub) => {
    const subCreatorNorm = normalizeUsername(sub?.createdBy);
    if (subCreatorNorm && myNormUser && subCreatorNorm === myNormUser) return true;
    if (isCreator) return true;
    return false;
  };

  const completedSubtasksCount = subtasks.filter(s => (s.subTaskStatus || s.taskStatus) === 'COMPLETED').length;
  const totalSubtasksCount = subtasks.length;

  // Resolve display name for linked project or product
  const linkedProjectName = task?.projectName ||
    task?.project?.projectName ||
    task?.project?.name ||
    projectsList.find(p => String(p.id) === String(task?.projectId))?.projectName ||
    projectsList.find(p => String(p.id) === String(task?.projectId))?.name;

  const linkedProductName = task?.productName ||
    task?.product?.productname ||
    task?.product?.name ||
    productsList.find(p => String(p.id) === String(task?.productId))?.productname ||
    productsList.find(p => String(p.id) === String(task?.productId))?.name;

  return (
    <div className="max-w-5xl mx-auto space-y-5 animate-fade-in pb-20 px-4">
      
      {/* Top Action Bar: Back Button on Left, Edit & Delete on Right */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          onClick={() => {
            if (location.state?.from) {
              navigate(location.state.from);
            } else if (paramProjectId) {
              navigate(`/ProjectDetails/${paramProjectId}`);
            } else if (location.pathname.startsWith('/TasksView')) {
              navigate('/TasksView');
            } else if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/my-space/tasks');
            }
          }}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg shadow-xs transition-all hover:border-slate-300 cursor-pointer"
          title="Return to previous page"
        >
          <FiArrowLeft className="text-slate-500 text-sm" />
          <span>
            {location.state?.from?.toLowerCase().includes('project') || paramProjectId
              ? 'Back to Project'
              : 'Back to Tasks'}
          </span>
        </button>

        <div className="flex items-center gap-2">
          {/* Edit Task Button - Restricted to Assignee & Creator */}
          {canEdit && (
            <button
              onClick={() => setIsEditDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg shadow-xs transition-all hover:border-slate-300 cursor-pointer"
              title="Edit task details"
            >
              <FiEdit2 className="text-slate-500 text-sm" />
              <span>Edit Task</span>
            </button>
          )}

          {/* Delete Task Button */}
          {canDelete && (
            <button
              onClick={() => setDeleteTaskConfirm({ open: true, loading: false })}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg shadow-xs transition-all cursor-pointer"
              title="Delete this task"
            >
              <FiTrash2 className="text-rose-500 text-sm" />
              <span>Delete Task</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Task Card */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
        
        {/* Task Header & Metadata */}
        <div className="p-6 sm:p-7 border-b border-slate-100 space-y-4">
          
          {/* Top Row: Priority Badge, Task ID, and Clean Status Dropdown */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            
            {/* Light, Minimal Pastel Priority Badge & Task ID (No Arrow Icon) */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${pm.bg} ${pm.color} ${pm.border}`}>
                <span>{pm.label} Priority</span>
              </span>

              <span className="text-xs text-slate-400 font-mono font-medium ml-1">
                #TASK-{task.taskId}
              </span>

              {linkedProjectName && (
                <span className="text-[11px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 truncate max-w-[160px]" title={`Project: ${linkedProjectName}`}>
                  {linkedProjectName}
                </span>
              )}
              {linkedProductName && (
                <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 truncate max-w-[160px]" title={`Product: ${linkedProductName}`}>
                  {linkedProductName}
                </span>
              )}
            </div>

            {/* Clean Status Dropdown (Pic 4: Sleek aligned rounded-lg & chevron shifted inward) */}
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Status:
              </span>
              <div className="relative inline-flex items-center">
                <select
                  value={task.taskStatus || 'TO_DO'}
                  onChange={e => handleUpdateStatus(e.target.value)}
                  disabled={!canEdit}
                  className={`appearance-none pl-3 pr-8 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:border-slate-400 shadow-2xs ${
                    !canEdit ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:border-slate-300'
                  }`}
                  title={canEdit ? 'Change status' : 'Editing is restricted to the assignee or creator'}
                >
                  <option value="TO_DO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 stroke-[2.5]" />
              </div>
            </div>

          </div>

          {/* Title */}
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug tracking-tight">
            {task.title}
          </h1>

          {/* Optional Description */}
          {task.description && (
            <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200/60 text-xs text-slate-700 leading-relaxed font-normal whitespace-pre-line">
              {task.description}
            </div>
          )}

          {/* Attributes Grid (Constant slot positions) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 text-xs">
            
            {/* Slot 1: Assignee */}
            <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200/60">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block">
                  Assignee
                </span>
                {me && task.username !== me && task.taskStatus !== 'COMPLETED' && (
                  <button
                    type="button"
                    onClick={handleSelfAssign}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-800 cursor-pointer"
                    title="Self-assign this task to me"
                  >
                    <span>Assign to me</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <Avatar name={task.username} size={22} />
                <span className={`truncate font-semibold ${task.username ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {task.username || 'Unassigned'}
                </span>
              </div>
            </div>

            {/* Slot 2: Due Date */}
            <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200/60">
              <span className={`text-[10px] uppercase tracking-wider font-semibold block mb-1 ${isOverdue ? 'text-rose-500' : 'text-slate-400'}`}>
                {isOverdue ? 'Overdue' : 'Due Date'}
              </span>
              <div className={`flex items-center gap-1.5 font-semibold ${isOverdue ? 'text-rose-600' : 'text-slate-700'}`}>
                <FiCalendar className={`shrink-0 ${isOverdue ? 'text-rose-500' : 'text-slate-400'}`} />
                <span className="truncate">{task.deadLine ? fmtDate(task.deadLine) : 'No Deadline'}</span>
              </div>
            </div>

            {/* Slot 3: Created By */}
            <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200/60">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block mb-1">
                Created By
              </span>
              <div className="flex items-center gap-1.5 font-semibold text-slate-700 min-w-0" title={task.createdBy || 'System'}>
                <FiUser className="text-slate-400 shrink-0 text-sm" />
                <span className="truncate">{task.createdBy ? String(task.createdBy).split('@')[0] : 'System'}</span>
              </div>
            </div>

            {/* Slot 4: Project / Product Display Name */}
            <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200/60">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block mb-1">
                {linkedProductName && !linkedProjectName ? 'Product' : 'Project'}
              </span>
              <div className="flex items-center gap-1.5 font-semibold text-slate-700 min-w-0">
                <FiFolder className="text-slate-400 shrink-0 text-sm" />
                <span className="truncate" title={linkedProjectName || linkedProductName || 'General'}>
                  {linkedProjectName || linkedProductName || (task.projectId ? `Project #${task.projectId}` : 'General')}
                </span>
              </div>
            </div>

          </div>

        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 bg-slate-50/50 px-4">
          <button
            onClick={() => setActiveTab('subtasks')}
            className={`px-4 py-3 text-xs font-semibold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'subtasks'
                ? 'border-blue-600 text-blue-700 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <TaskChecklistIcon size={14} className="text-sm" />
            <span>Subtasks</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold ${
              activeTab === 'subtasks' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {totalSubtasksCount}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('comments')}
            className={`px-4 py-3 text-xs font-semibold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'comments'
                ? 'border-blue-600 text-blue-700 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <FiMessageSquare className="text-sm" />
            <span>Discussion</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold ${
              activeTab === 'comments' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {comments.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('attachments')}
            className={`px-4 py-3 text-xs font-semibold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'attachments'
                ? 'border-blue-600 text-blue-700 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <FiPaperclip className="text-sm" />
            <span>Attachments</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold ${
              activeTab === 'attachments' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {attachments.length}
            </span>
          </button>
        </div>

        {/* Tab Contents */}
        <div className="p-6">
          
          {/* TAB 1: SUBTASKS */}
          {activeTab === 'subtasks' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-slate-800 text-sm">
                    Subtasks ({completedSubtasksCount}/{totalSubtasksCount} completed)
                  </h3>
                  <p className="text-xs text-slate-500">Track granular deliverables and status milestones.</p>
                </div>
                {canEdit && (
                  <button
                    onClick={() => setShowSubModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/60 rounded-lg text-xs font-semibold transition-all cursor-pointer shrink-0 self-start sm:self-auto"
                  >
                    <FiPlus className="text-sm" />
                    <span>Add Subtask</span>
                  </button>
                )}
              </div>

              {/* Subtask List */}
              {totalSubtasksCount === 0 ? (
                <div className="text-center py-10 bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
                  <TaskChecklistIcon size={26} className="text-slate-300 mx-auto mb-1.5" />
                  <p className="text-slate-500 text-xs font-medium">No subtasks added yet.</p>
                  <button
                    onClick={() => setShowSubModal(true)}
                    className="mt-2 text-xs text-blue-600 font-semibold hover:underline cursor-pointer"
                  >
                    + Add first subtask
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {subtasks.map(sub => {
                    const sId = sub.subTaskId || sub.id || sub.taskId;
                    const sTitle = sub.subTaskTitle || sub.title;
                    const sStatus = sub.subTaskStatus || sub.taskStatus || 'TO_DO';
                    const isCompleted = sStatus === 'COMPLETED';

                    return (
                      <div 
                        key={sId}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-slate-50/70 hover:bg-slate-100/70 rounded-xl border border-slate-200/70 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Status Toggle Box */}
                          <button
                            type="button"
                            onClick={() => handleUpdateSubtaskStatus(sId, isCompleted ? 'TO_DO' : 'COMPLETED', sTitle)}
                            title="Click to toggle status"
                            className={`w-5 h-5 rounded flex items-center justify-center border transition-all cursor-pointer shrink-0 ${
                              isCompleted
                                ? 'bg-emerald-500 border-emerald-600 text-white'
                                : sStatus === 'IN_PROGRESS'
                                ? 'bg-blue-500 border-blue-600 text-white'
                                : 'bg-white border-slate-300 hover:border-slate-400'
                            }`}
                          >
                            {isCompleted && <FiCheck className="text-xs" />}
                            {sStatus === 'IN_PROGRESS' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </button>

                          <div className="min-w-0">
                            <span className={`text-xs font-semibold truncate block ${isCompleted ? 'text-slate-700' : 'text-slate-800'}`}>
                              {sTitle}
                            </span>
                            {sub.description && (
                              <p className="text-[11px] text-slate-500 truncate mt-0.5">{sub.description}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                          {sub.deadLine && (
                            <span className="text-[11px] text-slate-500 flex items-center gap-1">
                              <FiCalendar className="text-slate-400" />
                              {fmtDate(sub.deadLine)}
                            </span>
                          )}

                          {/* Clean Subtask Status Dropdown */}
                          <select
                            value={sStatus}
                            onChange={(e) => handleUpdateSubtaskStatus(sId, e.target.value, sTitle)}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-lg border cursor-pointer outline-none transition-all ${
                              isCompleted
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : sStatus === 'IN_PROGRESS'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-slate-50 text-slate-700 border-slate-200'
                            }`}
                          >
                            <option value="TO_DO">To Do</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="COMPLETED">Completed</option>
                          </select>

                          {canDeleteSubtask(sub) && (
                            <button
                              onClick={() => handleDeleteSubtask(sId, sTitle)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors rounded-lg hover:bg-rose-50 cursor-pointer"
                              title="Delete Subtask"
                            >
                              <FiTrash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: MODERN DISCUSSION STREAM (Minimal, Professional, Useful) */}
          {activeTab === 'comments' && (
            <div className="space-y-6 max-w-3xl">
              {/* Discussion Stream */}
              <div className="space-y-3.5 max-h-[460px] overflow-y-auto pr-1 custom-scrollbar">
                {comments.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50/60 rounded-2xl border border-dashed border-slate-200">
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-2 text-slate-400 shadow-2xs">
                      <FiMessageSquare size={18} />
                    </div>
                    <p className="text-xs font-semibold text-slate-700">No discussion yet</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Start the conversation or post updates on this deliverable.</p>
                  </div>
                ) : (
                  comments.map((c, idx) => {
                    const commentText = typeof c === 'string' ? c : (c.description || c.comment || c.text || c.content || c.commentText || c.body || c.message || '');
                    const commentUser = c.username || c.user || c.author || 'Team Member';
                    const cId = c.commentId || c.id || idx;
                    const isAuthor = commentUser.toLowerCase() === me.toLowerCase();
                    const isAssignee = task.username && commentUser.toLowerCase() === task.username.toLowerCase();
                    const isCreator = task.createdBy && commentUser.toLowerCase() === task.createdBy.toLowerCase();
                    const initials = commentUser.slice(0, 2).toUpperCase();

                    return (
                      <div key={cId} className="group p-4 bg-white rounded-2xl border border-slate-200/90 shadow-2xs hover:border-slate-300 transition-all space-y-2">
                        {/* Header: Avatar, Name, Badges, Time, Actions */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-600">
                              {initials}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900 text-xs sm:text-sm">{commentUser}</span>
                              {isAuthor && (
                                <span className="px-1.5 py-0.2 bg-blue-50 text-blue-700 rounded text-[10px] font-semibold border border-blue-100">You</span>
                              )}
                              {!isAuthor && isAssignee && (
                                <span className="px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[10px] font-medium border border-slate-200">Assignee</span>
                              )}
                              {!isAuthor && isCreator && (
                                <span className="px-1.5 py-0.2 bg-purple-50 text-purple-700 rounded text-[10px] font-medium border border-purple-100">Creator</span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-[11px] text-slate-400">
                            <span>{c.createdAt || c.timestamp ? fmtDate(c.createdAt || c.timestamp) : ''}</span>
                            
                            {/* Action: Copy Text */}
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(commentText);
                                toast.success('Comment copied to clipboard');
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-700 rounded transition-all cursor-pointer"
                              title="Copy text"
                            >
                              <FiCopy size={12} />
                            </button>

                            {/* Action: Delete */}
                            {(isAuthor || isSuperAdmin || isAdmin) && (c.commentId || c.id) && (
                              <button
                                type="button"
                                onClick={() => handleDeleteComment(c.commentId || c.id)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-all cursor-pointer"
                                title="Delete comment"
                              >
                                <FiTrash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Comment Body */}
                        <p className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-wrap pl-9">
                          {commentText || '(No content)'}
                        </p>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Modern Minimal Composer Box */}
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-3.5 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100/60 transition-all">
                <textarea
                  rows={2}
                  placeholder="Write a message, share progress, or leave notes for the team..."
                  value={commentDraft}
                  onChange={e => setCommentDraft(e.target.value)}
                  onKeyDown={e => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && commentDraft.trim()) {
                      e.preventDefault();
                      handlePostComment();
                    }
                  }}
                  className="w-full bg-transparent border-none outline-none text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 resize-none leading-relaxed"
                />

                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] text-slate-400 hidden sm:inline">
                    Tip: Press <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono font-semibold">Ctrl + Enter</kbd> to post
                  </span>

                  <button
                    type="button"
                    onClick={handlePostComment}
                    disabled={!commentDraft.trim()}
                    className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-xl font-semibold text-xs hover:bg-blue-700 disabled:opacity-40 transition-all cursor-pointer shadow-xs"
                  >
                    <FiSend size={12} />
                    <span>Post Message</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: ATTACHMENTS */}
          {activeTab === 'attachments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 text-sm">Task Attachments</h3>
                  <p className="text-xs text-slate-500">Supporting documents, assets, and reports.</p>
                </div>
                
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/60 rounded-lg text-xs font-semibold transition-all">
                  <FiPlus className="text-sm" /> <span>{uploading ? 'Uploading...' : 'Add Attachment'}</span>
                  <input
                    type="file"
                    multiple
                    disabled={uploading}
                    className="hidden"
                    onChange={handleAddAttachment}
                  />
                </label>
              </div>

              {attachments.length === 0 ? (
                <div className="text-center py-10 bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
                  <FiPaperclip className="text-2xl text-slate-300 mx-auto mb-1.5" />
                  <p className="text-slate-500 text-xs font-medium">No attachments uploaded yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {attachments.map(att => (
                    <div key={att.id || att.fileName} className="p-3 bg-slate-50/70 rounded-xl border border-slate-200/70 flex items-center justify-between">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center text-sm shrink-0">
                          <FiFileText />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-slate-800 truncate">{att.fileName}</div>
                          <div className="text-[10px] text-slate-400">{att.uploadedAt ? fmtDate(att.uploadedAt) : ''}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        {att.fileUrl && (
                          <>
                            <button
                              onClick={() => setPreviewFile(att)}
                              className="px-2.5 py-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center gap-1 cursor-pointer"
                              title="Preview file"
                            >
                              <FiEye size={12} /> View
                            </button>
                            <a
                              href={att.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              download={att.fileName}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Download file"
                            >
                              <FiDownload size={13} />
                            </a>
                          </>
                        )}
                        {att.id && (
                          <button
                            onClick={() => handleDeleteAttachment(att.id, att.fileName)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="Delete attachment"
                          >
                            <FiTrash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

      </div>

      {/* Edit Task Side Drawer - Restricted to Assignee & Creator */}
      {canEdit && (
        <CreateTaskModal
          isOpen={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          onSave={handleSaveEditTask}
          initialData={task}
          isEdit={true}
        />
      )}

      {/* Add Subtask Modal - Portaled to document.body so it centers on the full screen */}
      {showSubModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs transition-opacity"
            onClick={() => setShowSubModal(false)}
            aria-hidden="true"
          />

          {/* Centered Modal Card */}
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4 my-auto border border-slate-200 z-10">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-1.5"><TaskChecklistIcon size={16} className="text-blue-600" /><span>Add Subtask</span></h3>
              <button 
                onClick={() => setShowSubModal(false)} 
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <FiX className="text-base" />
              </button>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Subtask Title *
              </label>
              <input
                type="text"
                placeholder="e.g. Protocol review"
                value={subtaskTitle}
                onChange={e => setSubtaskTitle(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-slate-400 transition-all"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Description
              </label>
              <textarea
                placeholder="Details or deliverables..."
                value={subtaskDescription}
                onChange={e => setSubtaskDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-slate-400 transition-all resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Status
                </label>
                <select
                  value={subtaskStatus}
                  onChange={e => setSubtaskStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-slate-400 transition-all cursor-pointer"
                >
                  <option value="TO_DO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Deadline
                </label>
                <input
                  type="date"
                  value={subtaskDeadLine}
                  onChange={e => setSubtaskDeadLine(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-slate-400 transition-all"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() => setShowSubModal(false)}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSubtask}
                className="px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-xs cursor-pointer"
              >
                Save Subtask
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PDF / Document Preview Modal - Portaled to document.body */}
      {previewFile && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-4 bg-slate-900/50 backdrop-blur-xs"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="bg-white rounded-xl w-[96vw] max-w-[1760px] h-[95vh] shadow-2xl flex flex-col overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
              <div className="flex items-center gap-2.5 min-w-0 pr-4">
                <FiFileText className="text-blue-600 shrink-0 text-base" />
                <h3 className="font-semibold text-slate-800 truncate text-xs sm:text-sm" title={previewFile.fileName}>
                  {previewFile.fileName}
                </h3>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={previewFile.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center gap-1 cursor-pointer"
                >
                  Open in New Tab
                </a>
                <a
                  href={previewFile.fileUrl}
                  download={previewFile.fileName}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors inline-flex items-center gap-1 cursor-pointer"
                >
                  <FiDownload size={12} /> Download
                </a>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ml-1"
                  title="Close (Esc)"
                >
                  <FiX className="text-base" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-white p-0 overflow-hidden relative">
              {previewFile.fileUrl ? (
                /\.(png|jpe?g|gif|webp|svg)$/i.test(previewFile.fileName || '') ? (
                  <div className="w-full h-full flex items-center justify-center p-2 bg-slate-50/50 overflow-auto">
                    <img
                      src={previewFile.fileUrl}
                      alt={previewFile.fileName}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <iframe
                    src={previewFile.fileUrl}
                    title={previewFile.fileName}
                    className="w-full h-full border-0 block"
                  />
                )
              ) : (
                <div className="text-center py-20 text-slate-500 text-xs">
                  <p>Preview not available for this file.</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm Delete Task Modal */}
      <ConfirmModal
        isOpen={deleteTaskConfirm.open}
        onClose={() => setDeleteTaskConfirm({ open: false, loading: false })}
        onConfirm={confirmDeleteTask}
        title="Delete Task?"
        message={`Are you sure you want to delete "${task?.title || 'this task'}"? All subtasks, comments, and attachments will also be permanently removed.`}
        confirmText="Delete"
        loading={deleteTaskConfirm.loading}
        variant="danger"
      />

    </div>
  );
}
